import { useEffect, useState } from 'react'
import { portalAPI } from '../lib/api'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import {
  Award, TrendingUp, TrendingDown, Gift, Star, Trophy,
  CheckCircle, AlertCircle, Sparkles, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react'

export default function PortalLoyaltyPage() {
  const [points, setPoints] = useState(0)
  const [tx, setTx] = useState<any[]>([])
  const [code, setCode] = useState('')
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    const res = await portalAPI.loyalty()
    setLoading(false)
    if (res.success) {
      setPoints((res.data as any).points || 0)
      setTx((res.data as any).transactions || [])
    }
  }

  async function applyReferral(e: React.FormEvent) {
    e.preventDefault()
    setMsg('')
    setError('')
    if (!code.trim()) {
      setError('Please enter a referral code')
      return
    }

    setLoading(true)
    const res = await portalAPI.applyReferral(code)
    setLoading(false)

    if (res.success) {
      setMsg('Referral code applied successfully! Points added.')
      setCode('')
      loadData()
    } else {
      setError(res.message || 'Failed to apply referral code')
    }
  }

  const earnedPoints = tx.filter(t => t.type === 'earn').reduce((sum, t) => sum + t.points, 0)
  const spentPoints = tx.filter(t => t.type === 'redeem').reduce((sum, t) => sum + t.points, 0)

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Loyalty & Rewards
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Track your points and redeem rewards</p>
        </div>

        {/* Messages */}
        {msg && (
          <div className="p-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-2">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
            <p className="text-sm font-medium text-green-800 dark:text-green-200">{msg}</p>
          </div>
        )}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
            <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Points Summary */}
        <div className="grid md:grid-cols-3 gap-4">
          {/* Total Points */}
          <div className="card bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10 border-2 border-orange-200 dark:border-orange-800">
            <div className="card-body">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-orange-600 dark:text-orange-400 flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Total Points
                  </p>
                  <p className="text-4xl font-bold mt-2 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                    {loading ? (
                      <span className="inline-block w-24 h-10 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></span>
                    ) : (
                      points
                    )}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Available to redeem</p>
                </div>
                <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 shadow-lg">
                  <Trophy className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Earned Points */}
          <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/5 dark:to-emerald-500/5 border-green-200 dark:border-green-800">
            <div className="card-body">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600 dark:text-green-400 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Points Earned
                  </p>
                  <p className="text-3xl font-bold mt-2 text-green-900 dark:text-green-100">
                    {loading ? (
                      <span className="inline-block w-20 h-9 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></span>
                    ) : (
                      `+${earnedPoints}`
                    )}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500">
                  <Star className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Redeemed Points */}
          <div className="card bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-500/5 dark:to-pink-500/5 border-purple-200 dark:border-purple-800">
            <div className="card-body">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400 flex items-center gap-2">
                    <TrendingDown className="h-4 w-4" />
                    Points Redeemed
                  </p>
                  <p className="text-3xl font-bold mt-2 text-purple-900 dark:text-purple-100">
                    {loading ? (
                      <span className="inline-block w-20 h-9 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"></span>
                    ) : (
                      spentPoints
                    )}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500">
                  <Gift className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Referral Code */}
        <div className="card border-2 border-orange-200 dark:border-orange-800">
          <div className="card-header flex items-center gap-2 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-500/10 dark:to-red-500/10">
            <Gift className="h-5 w-5 text-orange-600" />
            <h2 className="font-semibold text-lg">Apply Referral Code</h2>
          </div>
          <div className="card-body">
            <form onSubmit={applyReferral} className="space-y-4">
              <div>
                <label className="form-label flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-gray-500" />
                  Referral Code
                </label>
                <div className="flex gap-3">
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter your referral code..."
                    disabled={loading}
                    className="flex-1 text-lg font-mono uppercase"
                  />
                  <Button
                    type="submit"
                    disabled={loading || !code.trim()}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    {loading ? 'Applying...' : 'Apply Code'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter a friend's referral code to earn bonus points
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Transaction History */}
        <div className="card">
          <div className="card-header flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-600" />
            <h2 className="font-semibold text-lg">Points History</h2>
            <span className="ml-auto px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300">
              {tx.length} transactions
            </span>
          </div>
          <div className="card-body">
            {loading && tx.length === 0 ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : tx.length === 0 ? (
              <div className="text-center py-12">
                <Award className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No Transaction History</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Start earning points by making bookings
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {tx.map(t => (
                  <div
                    key={t.id}
                    className="p-3 rounded-lg border-2 border-gray-200 dark:border-gray-800 hover:border-orange-300 dark:hover:border-orange-700 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`p-2 rounded-lg ${
                          t.type === 'earn'
                            ? 'bg-green-100 dark:bg-green-500/10'
                            : 'bg-purple-100 dark:bg-purple-500/10'
                        }`}>
                          {t.type === 'earn' ? (
                            <ArrowUpRight className="h-5 w-5 text-green-600 dark:text-green-400" />
                          ) : (
                            <ArrowDownRight className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium capitalize">{t.type}</span>
                            {t.reason && (
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                · {t.reason}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-500 flex items-center gap-1 mt-0.5">
                            <Calendar className="h-3 w-3" />
                            {new Date(t.created_at).toLocaleString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                      <div className={`text-lg font-bold ${
                        t.type === 'earn'
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-purple-700 dark:text-purple-300'
                      }`}>
                        {t.type === 'earn' ? '+' : '-'}{t.points}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Info Banner */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-xl">
          <Star className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="flex-1 text-sm">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">How to Earn Points</h3>
            <p className="text-blue-800 dark:text-blue-300">
              Earn points by making bookings, referring friends, and engaging with our studio.
              Points can be redeemed for discounts on future bookings and exclusive perks.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
